import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';

export type TListSpaceConfig = {
  spaceId: string;
  spaceName: string;
  activeCount: number;
};

export type TGetListSpaceConfigResponse = ApiResponse<TListSpaceConfig[]>;

export const getListSpaceConfig = async (): Promise<TGetListSpaceConfigResponse> => {
  const url = `/notifications/space-config-count`;

  return axiosInstance.get(url);
};

export const GET_LIST_SPACE_CONFIG_QUERY_KEY = 'get-list-space-config';

export const getListSpaceConfigQueryOptions = <TData = TGetListSpaceConfigResponse>(
  select?: (data: TGetListSpaceConfigResponse) => TData
) => {
  return queryOptions({
    queryKey: [GET_LIST_SPACE_CONFIG_QUERY_KEY],
    queryFn: () => getListSpaceConfig(),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListSpaceConfigQuery<TData>(options: {
  queryConfig?: QueryConfig<typeof getListSpaceConfigQueryOptions<TData>>;
  select: (data: TGetListSpaceConfigResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListSpaceConfigQuery(options: {
  queryConfig?: QueryConfig<typeof getListSpaceConfigQueryOptions<TGetListSpaceConfigResponse>>;
}): ReturnType<typeof useQuery<TGetListSpaceConfigResponse>>;

// Implementation
export function useListSpaceConfigQuery<TData = TGetListSpaceConfigResponse>({
  queryConfig,
  select,
}: {
  queryConfig?: QueryConfig<typeof getListSpaceConfigQueryOptions<TData | TGetListSpaceConfigResponse>>;
  select?: (data: TGetListSpaceConfigResponse) => TData;
}) {
  return useQuery({
    ...getListSpaceConfigQueryOptions(select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListSpaceConfigResponse ? TGetListSpaceConfigResponse : TData>>;
}
